import React, { useState, useCallback } from "react";
import { Card, Form, InputGroup, Button } from "react-bootstrap";
import api from "../../api";
import DataQualityNotice from "../../components/dashboard/DataQualityNotice";
import MetricTrendChart from "../../components/dashboard/MetricTrendChart";
import MetricBreakdownTable from "../../components/dashboard/MetricBreakdownTable";
import "./ProjectPerformanceDashboardPage.css";

// Phase 5, §30. "Project Dashboard" — requires dashboards:view_project +
// real project access (re-validated server-side). project_id is a
// required filter here, unlike the other dashboards.
const FORMS_COLUMNS = [
  { key: "userId", label: "User ID" }, { key: "formsAssigned", label: "Assigned" }, { key: "formsSubmitted", label: "Submitted" }, { key: "avgEligibleScore", label: "Avg Score %" },
];
const UAT_COLUMNS = [
  { key: "userId", label: "User ID" }, { key: "uatSubmissions", label: "Submissions" }, { key: "avgPassRate", label: "Pass Rate %" }, { key: "linkedDefects", label: "Linked Defects" },
];

export default function ProjectPerformanceDashboardPage() {
  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/dashboards/project-summary", { params: { project_id: projectId } });
      setData(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load project dashboard.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const workloadLabels = (data?.workload || []).map((w) => w.statusLabel);
  const workloadValues = (data?.workload || []).map((w) => w.taskCount);

  // Phase 65 follow-up: form/UAT analytics key an EXTERNAL recipient (no
  // account) as 'external:<email>' -- shown as "External: <name or email>"
  // instead of a raw key; registered users keep their numeric id.
  const withUserId = (byUser) => Object.entries(byUser || {}).map(([userId, v]) => ({
    ...v,
    userId: v && v.isExternal ? `External: ${v.recipientName || v.recipientEmail || userId.replace(/^external:/, "")}` : userId,
  }));

  return (
    <div className="unit-page dashboard-shell project-perf-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Performance</div>
          <h2>Project Dashboard</h2>
          <p>Project task status, module breakdown, TAT, contribution, UAT and form compliance.</p>
        </div>
      </div>

      <div className="perf-filter-bar perf-filter-bar-compact">
        <Form.Label className="perf-filter-label" htmlFor="proj-dash-id">Project ID</Form.Label>
        <InputGroup className="perf-project-id-group">
          <Form.Control
            id="proj-dash-id"
            className="perf-filter-input"
            type="number"
            placeholder="Enter Project ID"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          />
          <Button variant="outline-primary" onClick={fetchSummary} disabled={!projectId}>
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            Load
          </Button>
        </InputGroup>
      </div>

      {error && (
        <div className="perf-error-state">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="perf-skeleton-stack">
          {[0, 1, 2].map((i) => <div key={i} className="perf-skeleton-block" />)}
        </div>
      ) : data ? (
        <>
          <DataQualityNotice warnings={data.warnings} />

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-chart-column" aria-hidden="true"></i>Task Status Breakdown</h6>
            <MetricTrendChart title="Tasks by status" labels={workloadLabels} values={workloadValues} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-clipboard-check" aria-hidden="true"></i>UAT Pass Rate &amp; Linked Defects</h6>
            <MetricBreakdownTable rows={withUserId(data.uatByUser)} columns={UAT_COLUMNS} />
          </Card.Body></Card>

          <Card className="perf-section-card"><Card.Body>
            <h6 className="perf-section-title"><i className="fa-solid fa-file-lines" aria-hidden="true"></i>Form Assignments &amp; Submissions</h6>
            <MetricBreakdownTable rows={withUserId(data.formsByUser)} columns={FORMS_COLUMNS} />
          </Card.Body></Card>
        </>
      ) : (
        <div className="perf-empty-state perf-empty-state-standalone">
          <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
          <p className="perf-empty-state-title">Select a project</p>
          <p>Enter a Project ID above to load its dashboard.</p>
        </div>
      )}
    </div>
  );
}
